(() => {
  const root = document.querySelector('[data-co2-calculator]');
  if (!root) return;

  const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
  const percentFormatter = new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 0 });
  const KG_PER_LITER_GAS = 2.31;

  const fallback = {
    defaults: {
      distance_km: 8,
      trips_per_week: 5,
      weeks_per_year: 48,
    car_kg_per_km: 0.192,
    ebike_wh_per_km: 8,
    grid_kg_per_kwh: 0.55
  },
    vehicles: [
      { id: 'sedan', label: 'Family sedan', kg_per_km: 0.192 },
      { id: 'suv', label: 'SUV / MPV', kg_per_km: 0.241 },
      { id: 'bus', label: 'Bus (per rider)', kg_per_km: 0.082 }
    ],
    presets: [
      { label: 'Urban commute', distance_km: 6, trips_per_week: 5 },
      { label: 'Weekend city ride', distance_km: 12, trips_per_week: 2 }
    ],
    copy: {
      title: 'CO₂ Ride Emission Calculator',
      subtitle: 'See how much CO₂ you cut by riding FAFREES instead of driving.',
      cta: 'Calculate',
      ebike_label: 'FAFREES E-Bike',
      car_label: 'Driving / Public'
    }
  };

  const els = {
    distance: root.querySelector('[data-distance]'),
    frequency: root.querySelector('[data-frequency]'),
    vehicleSelect: root.querySelector('[data-vehicle]'),
    vehicleList: root.querySelector('[data-vehicle-list]'),
    distanceNote: root.querySelector('[data-distance-note]'),
    frequencyNote: root.querySelector('[data-frequency-note]'),
    presets: root.querySelector('[data-presets]'),
    saving: root.querySelector('[data-saving]'),
    trees: root.querySelector('[data-trees]'),
    treesBig: root.querySelector('[data-trees-big]'),
    treesMonth: root.querySelector('[data-tree-month]'),
    kmAvoided: root.querySelector('[data-km-avoided]'),
    fuelSaved: root.querySelector('[data-fuel-saved]'),
    treeVisual: root.querySelector('[data-tree-visual]'),
    carValue: root.querySelector('[data-car]'),
    ebikeValue: root.querySelector('[data-ebike]'),
    reduction: root.querySelector('[data-reduction]'),
    carBar: root.querySelector('[data-car-bar]'),
    ebikeBar: root.querySelector('[data-ebike-bar]'),
    cta: root.querySelector('[data-cta]'),
    copyTargets: root.querySelectorAll('[data-copy]'),
    faqToggles: document.querySelectorAll('[data-co2-faq-toggle]')
  };

  const mergeData = (incoming = {}) => ({
    defaults: { ...fallback.defaults, ...(incoming.defaults || {}) },
    vehicles: Array.isArray(incoming.vehicles) && incoming.vehicles.length ? incoming.vehicles : fallback.vehicles,
    presets: Array.isArray(incoming.presets) && incoming.presets.length ? incoming.presets : fallback.presets,
    copy: { ...fallback.copy, ...(incoming.copy || {}) }
  });

  const applyCopy = (copy) => {
    els.copyTargets.forEach((node) => {
      const key = node.dataset.copy;
      if (key && copy[key]) node.textContent = copy[key];
    });
    if (els.cta && copy.cta) els.cta.textContent = copy.cta;
  };

  const renderVehicles = (vehicles, defaultVehicle) => {
    // Radio/group layout
    if (els.vehicleList) {
      els.vehicleList.innerHTML = '';
      vehicles.forEach((vehicle, index) => {
        const wrapper = document.createElement('label');
        wrapper.className = 'vehicle-option';

        const input = document.createElement('input');
        input.type = 'radio';
        input.name = 'co2-vehicle';
        input.value = vehicle.kg_per_km;
        input.checked = index === 0 || vehicle.id === defaultVehicle;
        input.setAttribute('aria-label', vehicle.label);

        const text = document.createElement('span');
        text.className = 'vehicle-option__label';
        text.textContent = vehicle.label;

        wrapper.appendChild(input);
        wrapper.appendChild(text);
        els.vehicleList.appendChild(wrapper);
      });
    } else if (els.vehicleSelect) {
      // Fallback to select if present
      els.vehicleSelect.innerHTML = '';
      vehicles.forEach((vehicle, index) => {
        const option = document.createElement('option');
        option.value = vehicle.kg_per_km;
        option.textContent = vehicle.label;
        if (index === 0 || vehicle.id === defaultVehicle) option.selected = true;
        els.vehicleSelect.appendChild(option);
      });
    }
  };

  const renderPresets = (presets) => {
    if (!els.presets) return;
    els.presets.innerHTML = '';
    presets.forEach((preset) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'preset-btn';
      btn.textContent = preset.label;
      btn.addEventListener('click', () => {
        if (els.distance) els.distance.value = preset.distance_km;
        if (els.frequency) els.frequency.value = preset.trips_per_week;
        update();
      });
      els.presets.appendChild(btn);
    });
  };

  const calculate = (settings) => {
    const distance = Math.max(parseFloat(els.distance?.value || settings.defaults.distance_km), 0);
    const tripsPerWeek = Math.min(Math.max(parseInt(els.frequency?.value || settings.defaults.trips_per_week, 10), 0), 14);
    const radioVehicle = els.vehicleList?.querySelector('input[name="co2-vehicle"]:checked')?.value;
    const selectVehicle = els.vehicleSelect?.value;
    const carKgPerKm = Math.max(parseFloat(radioVehicle || selectVehicle || settings.defaults.car_kg_per_km), 0);
    const annualKm = distance * tripsPerWeek * (settings.defaults.weeks_per_year || 48);

    const carEmissionKg = annualKm * carKgPerKm;
    const ebikeKwh = (annualKm * settings.defaults.ebike_wh_per_km) / 1000;
    const ebikeEmissionKg = ebikeKwh * settings.defaults.grid_kg_per_kwh;
    const saving = Math.max(carEmissionKg - ebikeEmissionKg, 0);

    const reductionPercent = carEmissionKg ? (carEmissionKg - ebikeEmissionKg) / carEmissionKg : 0;

    return {
      annualKm,
      carEmissionKg,
      ebikeEmissionKg,
      saving,
      trees: saving / 21, // 21 kg CO₂ per tree per year
      treesPerMonth: saving / 21 / 12,
      fuelLitersAvoided: carEmissionKg / KG_PER_LITER_GAS,
      reductionPercent
    };
  };

  const updateBars = (carEmissionKg, ebikeEmissionKg) => {
    const max = Math.max(carEmissionKg, ebikeEmissionKg, 1);
    const carPercent = Math.min(carEmissionKg / max, 1);
    const ebikePercent = Math.min(ebikeEmissionKg / max, 1);
    if (els.carBar) els.carBar.style.width = percentFormatter.format(carPercent);
    if (els.ebikeBar) els.ebikeBar.style.width = percentFormatter.format(ebikePercent);
  };

  const renderTreeIcons = (count) => {
    if (!els.treeVisual) return;
    const capped = Math.min(Math.max(Math.round(count), 0), 12);
    const more = Math.round(count) - capped;
    els.treeVisual.innerHTML = '';
    const frag = document.createDocumentFragment();
    for (let i = 0; i < capped; i += 1) {
      const icon = document.createElement('span');
      icon.className = 'tree-chip';
      icon.innerHTML = `
        <svg viewBox="0 0 28 32" aria-hidden="true" focusable="false">
          <defs>
            <linearGradient id="treeChipGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stop-color="#2fd19b"/>
              <stop offset="100%" stop-color="#0f8a5f"/>
            </linearGradient>
          </defs>
          <polygon points="14 3 4 18 24 18" fill="url(#treeChipGrad)"/>
          <polygon points="14 9 6 20 22 20" fill="url(#treeChipGrad)" opacity="0.85"/>
          <rect x="12" y="18" width="4" height="11" rx="1.2" fill="#70492e"/>
        </svg>
      `;
      frag.appendChild(icon);
    }
    if (more > 0) {
      const extra = document.createElement('span');
      extra.className = 'tree-chip tree-chip--more';
      extra.textContent = `+${more}`;
      frag.appendChild(extra);
    }
    els.treeVisual.appendChild(frag);
  };

  const renderResults = (result) => {
    if (els.saving) els.saving.textContent = numberFormatter.format(result.saving);
    if (els.trees) els.trees.textContent = numberFormatter.format(result.trees);
    if (els.treesBig) els.treesBig.textContent = numberFormatter.format(result.trees);
    if (els.treesMonth) els.treesMonth.textContent = numberFormatter.format(result.treesPerMonth);
    if (els.kmAvoided) els.kmAvoided.textContent = numberFormatter.format(result.annualKm);
    if (els.fuelSaved) els.fuelSaved.textContent = numberFormatter.format(result.fuelLitersAvoided);
    renderTreeIcons(result.trees);
    if (els.carValue) els.carValue.textContent = `${numberFormatter.format(result.carEmissionKg)} kg`;
    if (els.ebikeValue) els.ebikeValue.textContent = `${numberFormatter.format(result.ebikeEmissionKg)} kg`;
    if (els.reduction) {
      const ratio = result.carEmissionKg ? result.ebikeEmissionKg / result.carEmissionKg : 0;
      els.reduction.textContent = percentFormatter.format(Math.max(ratio, 0));
    }
    updateBars(result.carEmissionKg, result.ebikeEmissionKg);
  };

  const update = () => {
    renderResults(calculate(state));
  };

  const showNote = (element, message) => {
    if (!element) return;
    if (message) {
      element.textContent = message;
      element.hidden = false;
    } else {
      element.hidden = true;
      element.textContent = '';
    }
  };

  const applyLimits = () => {
    const limits = {
      maxDistance: 300,
      maxFrequency: 7
    };

    // Distance clamp and note
    if (els.distance) {
      let val = parseFloat(els.distance.value || state.defaults.distance_km) || 0;
      if (val < 0) val = 0;
      // Fun notes by range
      if (val === 0) {
        showNote(els.distanceNote, 'Pop in a distance to see your savings.');
      } else if (val > 0 && val <= 5) {
        showNote(els.distanceNote, 'Perfect for grocery runs and quick hops.');
      } else if (val > 5 && val <= 20) {
        showNote(els.distanceNote, 'Classic city commute territory—big CO₂ wins here.');
      } else if (val > 20 && val <= 80) {
        showNote(els.distanceNote, 'Solid daily or mixed-use mileage. Remember to check your battery plan.');
      } else if (val > 80 && val <= limits.maxDistance) {
        showNote(els.distanceNote, 'That’s an endurance ride—pack a charger and enjoy the scenery.');
      }
      if (val > limits.maxDistance) {
        val = limits.maxDistance;
        showNote(els.distanceNote, "That’s a huge ride for one leg—capped at 300 km for sanity. Break it into stages if needed.");
      } else {
        // Keep any range note already set; clear only if none applied
        // (handled above)
      }
      els.distance.value = val;
      state.defaults.distance_km = val;
    }

    // Frequency clamp and note
    if (els.frequency) {
      let freq = parseInt(els.frequency.value || state.defaults.trips_per_week, 10) || 0;
      if (freq < 0) freq = 0;
      if (freq > limits.maxFrequency) {
        freq = limits.maxFrequency;
        showNote(els.frequencyNote, "Weeks only have 7 days—save some rest time!");
      } else {
        showNote(els.frequencyNote, '');
      }
      els.frequency.value = freq;
      state.defaults.trips_per_week = freq;
    }
  };

  const bindEvents = () => {
    ['input', 'change'].forEach((evt) => {
      els.distance?.addEventListener(evt, () => { applyLimits(); update(); });
      els.frequency?.addEventListener(evt, () => { applyLimits(); update(); });
      els.vehicleSelect?.addEventListener(evt, update);
    });
    els.cta?.addEventListener('click', update);

    els.vehicleList?.addEventListener('change', (e) => {
      const target = e.target;
      if (target && target.name === 'co2-vehicle') {
        state.defaults.car_kg_per_km = parseFloat(target.value) || state.defaults.car_kg_per_km;
        update();
      }
    });

    els.faqToggles.forEach((btn) => {
      btn.addEventListener('click', () => {
        btn.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', btn.classList.contains('is-open') ? 'true' : 'false');
      });
    });
  };

  const state = mergeData();

  const init = (settings) => {
    applyCopy(settings.copy);
    renderVehicles(settings.vehicles, settings.defaults.default_vehicle);
    renderPresets(settings.presets);

    if (els.distance) els.distance.value = settings.defaults.distance_km;
    if (els.frequency) els.frequency.value = settings.defaults.trips_per_week;
    if (els.vehicleSelect) els.vehicleSelect.value = settings.defaults.car_kg_per_km;
    if (els.vehicleList) {
      const firstRadio = els.vehicleList.querySelector('input[name="co2-vehicle"]');
      if (firstRadio) firstRadio.checked = true;
    }

    applyLimits();

    bindEvents();
    update();
  };

  const loadConfig = async () => {
    const url = root.dataset.config;
    if (!url) {
      init(state);
      return;
    }
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('Config fetch failed');
      const json = await res.json();
      Object.assign(state, mergeData(json));
    } catch (e) {
      // Fallback stays
    }
    init(state);
  };

  loadConfig();
})();
